import React, { useState } from "react";
import { Box, Grid } from "@mui/material";
import { styles } from "../styles";
import { DATA_REPORTS_TABLE_HEADERS } from "../../../../utils/constants";
import { fontWeight, ICON_LABELS } from "../../../../utils/constants/UI";
import CustomTable from "../../../../components/Table/CustomTable";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import { GREY, BASE, BORDER } from "../../../../utils/constants/colors";
import ConsolidatedReportLog from "./ConsolidatedReportLog";
import CircularLoader from "../../../../components/Loaders/CircularLoader";
import {
  getRequest,
  postRequest,
  putRequest,
  deleteRequest,
} from "../../../../Apis/apiWrapper";
import { dailyReportEndpoints } from "../../../../utils/constants/httpConstants";
import { useNavigate } from "react-router-dom";
import CustomModal from "../../../../components/Modals/CustomModal";
import { Button } from "../../../../components/Buttons";

const ViewReports = () => {
  const classes = styles();
  const navigate = useNavigate();
  const [previewData, setPreviewData] = useState([]);
  const [isLogLoading, setIsLogLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const fetchReports = async () => {
    setIsTableLoading(true);
    try {
      const response = await getRequest(
        navigate,
        dailyReportEndpoints.getSummary,
      );
      if (response?.data?.success || response?.data?.status) {
        const mappedData = (response.data.data.summary || []).map((item) => ({
          ...item,
          ans_calls: item.calls?.answered_calls || 0,
          un_ans_calls: item.calls?.un_ans_calls || 0,
          emails: item.emails?.num_of_email_sent || 0,
          responses: item.responses?.num_of_res || 0,
          li_connects: item.linkedIn?.connection_linkedIn || 0,
          li_sent: item.linkedIn?.sent_msg_linkedIn || 0,
          li_replies: item.linkedIn?.respond_linkedIn || 0,
          meetings: item.meetings?.total || 0,
        }));
        setPreviewData(mappedData);
      }
    } catch (err) {
      console.error("Error fetching reports", err);
    } finally {
      setIsTableLoading(false);
    }
  };

  React.useEffect(() => {
    fetchReports();
  }, []);

  const handleSaveConsolidatedLog = async (payload) => {
    setIsLogLoading(true);
    try {
      const isEdit = !!editItem;
      const endpoint = isEdit
        ? dailyReportEndpoints.editConsolidated
        : dailyReportEndpoints.consolidatedReport;

      const response = isEdit
        ? await putRequest(navigate, endpoint, payload)
        : await postRequest(navigate, endpoint, payload);

      if (response?.data?.success || response?.data?.status) {
        setEditItem(null);
        fetchReports();
        return true;
      }
    } catch (err) {
      console.error("Error saving consolidated log", err);
      return false;
    } finally {
      setIsLogLoading(false);
    }
  };

  const handleEditReport = (item) => {
    setEditItem(item);
  };

  const handleDeleteReport = (item) => {
    setDeleteItem(item);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteReport = async () => {
    if (!deleteItem) return;
    setIsDeleteLoading(true);
    try {
      const endpoint = `${dailyReportEndpoints.deleteConsolidated}?id=${deleteItem._id}`;
      const response = await deleteRequest(endpoint);
      if (response?.data?.success || response?.data?.status) {
        setIsDeleteModalOpen(false);
        setDeleteItem(null);
        fetchReports();
      }
    } catch (err) {
      console.error("Error deleting report", err);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const TableData = {
    heads: DATA_REPORTS_TABLE_HEADERS.filter((h) => h.id !== "meetings"),
    body: previewData,
    isLoading: isTableLoading,
    total_records: previewData.length,
    is_selection_not_allowed: true,
    onEdit: handleEditReport,
    onDelete: handleDeleteReport,
    operations: [
      {
        id: "edit",
        label: "Edit",
        iconLabel: ICON_LABELS.EDIT,
        function: handleEditReport,
      },
      {
        id: "delete",
        label: "Delete",
        iconLabel: ICON_LABELS.DELETE,
        function: handleDeleteReport,
      },
    ],
  };

  return (
    <Box
      className={classes.container}
      style={{
        margin: "16px",
        height: "calc(100vh - 7rem)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        className={classes.componentContainer}
        style={{
          background: "white",
          borderRadius: "0px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxShadow: "none",
          border: `1px solid ${BORDER.DEFAULT}`,
          margin: 0,
        }}
      >
        <Box
          className={classes.flexBetween}
          style={{
            padding: "20px 24px",
            borderBottom: `2px solid ${BORDER.DEFAULT}`,
            backgroundColor: "#fff",
          }}
        >
          <Box
            className={classes.padUs}
            style={{ paddingTop: 0, paddingLeft: 0 }}
          >
            <TypographyInline
              size={"lg"}
              color={GREY.PRIMARY}
              fontWeight={fontWeight.BOLD}
              label="Add ActivityLogs"
            />
          </Box>
        </Box>

        <Box
          style={{
            flex: 1,
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Grid
            container
            spacing={0}
            style={{
              flex: 1,
              border: `1px solid ${BORDER.DEFAULT}`,
              height: "100%",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Grid
              item
              xs={10}
              style={{
                borderRight: `1px solid ${BORDER.DEFAULT}`,
                backgroundColor: "#fff",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <Box
                style={{
                  flex: 1,
                  backgroundColor: BASE.WHITE,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                  minHeight: 0,
                }}
              >
                <CustomTable tableData={TableData} />
              </Box>
            </Grid>
            <Grid
              item
              xs={2}
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <Box
                style={{
                  flex: 1,
                  backgroundColor: "#f8f9fa",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <ConsolidatedReportLog
                  onSave={handleSaveConsolidatedLog}
                  isLoading={isLogLoading}
                  editData={editItem}
                  onCancel={() => setEditItem(null)}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <CustomModal
        open={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        header={{ heading: { label: "Confirm Deletion" } }}
        width="400px"
      >
        <Box>
          <TypographyInline
            size="md"
            label="Are you sure you want to delete this report? This action cannot be undone."
            color={GREY.PRIMARY}
          />
          <Box style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
            <Button
              label="Cancel"
              variant="ghost"
              size="md40"
              fullWidth
              onClick={() => setIsDeleteModalOpen(false)}
            />
            <Button
              label="Delete"
              variant="primary"
              size="md40"
              fullWidth
              onClick={confirmDeleteReport}
              isLoading={isDeleteLoading}
            />
          </Box>
        </Box>
      </CustomModal>
    </Box>
  );
};

export default ViewReports;
